import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { Modulo, ModuloRota } from 'src/app/shared/enums/domain/modulo';
import { ArmazenamentoDeUsuarioService } from 'src/app/shared/services/armazenamento/armazenamento-de-usuario.service';
import {
  Notificacao,
  NotificacaoApiService,
} from 'src/app/shared/services/http/notificacao-api.service';

/**
 * O sino do topo do painel.
 *
 * Existe porque nada avisava ninguém: o corretor não sabia que o cliente
 * autorizou, o analista não sabia que uma proposta entrou na fila. Com o envio
 * para análise sendo um passo manual, uma proposta autorizada esperava alguém
 * lembrar de olhar.
 */
@Component({
  selector: 'app-notificacoes-sino',
  standalone: true,
  imports: [CommonModule, BsDropdownModule],
  templateUrl: './notificacoes-sino.component.html',
  styleUrl: './notificacoes-sino.component.scss',
})
export class NotificacoesSinoComponent implements OnInit, OnDestroy {
  private readonly api = inject(NotificacaoApiService);
  private readonly usuarios = inject(ArmazenamentoDeUsuarioService);
  private readonly router = inject(Router);

  protected readonly notificacoes = this.api.notificacoes;
  protected readonly naoLidas = this.api.naoLidas;
  protected readonly aberto = signal(false);

  /** Passou de 99 o número quebra o desenho do badge e deixa de informar. */
  protected readonly contador = computed(() => {
    const total = this.naoLidas();

    return total > 99 ? '99+' : String(total);
  });

  protected readonly vazio = computed(() => this.notificacoes().length === 0);

  ngOnInit(): void {
    this.api.iniciarPolling();
  }

  ngOnDestroy(): void {
    this.api.pararPolling();
  }

  protected icone(tipo: string): string {
    switch (tipo) {
      case 'proposta.autorizada':
        return 'bx bx-check-circle';
      case 'proposta.recusada':
        return 'bx bx-x-circle';
      case 'proposta.em-analise':
        return 'bx bx-search-alt';
      case 'proposta.devolvida':
        return 'bx bx-undo';
      case 'proposta.aprovada':
        return 'bx bx-badge-check';
      default:
        return 'bx bx-bell';
    }
  }

  protected abrir(notificacao: Notificacao): void {
    if (!notificacao.lida) {
      this.api.aplicarLeitura(notificacao.id);
      this.api.marcarLida(notificacao.id).subscribe({ error: () => undefined });
    }

    this.aberto.set(false);

    const destino = this.rotaDaProposta(notificacao.proposta_id);

    if (destino) {
      this.router.navigate(destino);
    }
  }

  protected marcarTodasLidas(): void {
    if (this.naoLidas() === 0) {
      return;
    }

    this.api.aplicarLeituraDeTodas();
    this.api.marcarTodasLidas().subscribe({ error: () => undefined });
  }

  /**
   * O caminho da proposta muda com o módulo de quem recebeu o aviso: o corretor
   * abre o formulário, o analista abre a tela de análise. Módulo sem tela de
   * proposta própria não navega — o aviso só marca como lido.
   */
  private rotaDaProposta(propostaId: number | null): string[] | null {
    if (!propostaId) {
      return null;
    }

    const moduloId = Number(this.usuarios.buscarUsuario()?.modulo?.id);

    if (moduloId === Modulo.VENDAS) {
      return [
        '/sistema',
        ModuloRota[Modulo.VENDAS],
        'propostas',
        'abertas',
        'formulario',
        String(propostaId),
      ];
    }

    if (moduloId === Modulo.ANALISE) {
      return ['/sistema', ModuloRota[Modulo.ANALISE], 'propostas', String(propostaId)];
    }

    return null;
  }
}
